import React, {FC, useEffect, useRef} from 'react';
import {connect} from 'react-redux';

import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {useAppDispatch} from '../../helpers/reduxHelpers';
import {makeMapStateToProps, ResolveStateProps} from '../../helpers/reselectHelpers';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {AppState} from '../../store/appState';
import {getIsUserVisitor} from '../../store/data/teammates/teammatesSelectors';
import {
  ContactCardParentPanelId,
  fetchContactCardAccountDataAsyncAction,
  navigateContactCardAction,
} from '../../store/panels/contacts/contactCard/contactCardPanelActions';
import {
  makeGetContactCardPanel,
  makeGetContactCardSelectedCard,
} from '../../store/panels/contacts/contactCard/contactCardPanelSelectors';
import {ContactCardLocationsEnum} from '../../store/panels/contacts/contactCard/contactCardPanelState';
import {inLegacyTransientPanel} from '../legacyTransientPanel';
import {computeContactCardLocationTracking} from './common/contactCardTrackingHelpers';
import {ContactCard, ContactCardProps} from './contactCard';

/*
 * Props.
 */

interface ContactCardContainerProps extends ContactCardProps {
  parentPanelId: ContactCardParentPanelId;
  location: ContactCardLocationsEnum;
}

type ContactCardContainerComponentProps = ContactCardContainerProps &
  ResolveStateProps<typeof mapStateToProps>;

/*
 * Selector.
 */

function getPanelId(_state: AppState, props: ContactCardContainerProps) {
  return props.panelId;
}

/*
 * Component.
 */

const ContactCardContainerComponent: FC<ContactCardContainerComponentProps> = (props) => {
  const {panelId, visualContact, popoverProps, card, panel, isUnverified, isUserVisitor, location} = props;
  const dispatch = useAppDispatch();
  const hasTrackedLoad = useRef<boolean>(false);

  // Make sure we have the account loaded.
  useEffect(() => {
    if (
      panel.status === AsyncStatusesEnum.LOADED &&
      !panel.accountStatus &&
      card?.accountId &&
      !isUserVisitor
    ) {
      dispatch(fetchContactCardAccountDataAsyncAction(panelId, card.accountId));
    }
  }, [card?.accountId, dispatch, isUserVisitor, panel.accountStatus, panel.status, panelId]);

  // Track the card when it is loaded.
  useEffect(() => {
    if (hasTrackedLoad.current || !card) {
      return;
    }
    hasTrackedLoad.current = true;
    trackInterfaceEvent(InterfaceEventsEnum.CONTACT_CARD_VIEW, {
      has_associated_account: Boolean(card.accountId),
      namespace: card.namespace,
      location: computeContactCardLocationTracking(location),
    });
  }, [card, location]);

  return (
    <ContactCard
      panelId={panelId}
      popoverProps={popoverProps}
      visualContact={visualContact}
      isUnverified={isUnverified}
      isUserVisitor={isUserVisitor}
      location={location}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactCardContainerProps>()(() => {
  const getContactCardPanel = makeGetContactCardPanel<ContactCardContainerProps>(getPanelId);
  return {
    panel: getContactCardPanel,
    card: makeGetContactCardSelectedCard(),
    isUserVisitor: getIsUserVisitor,
  };
});

export const ContactCardContainer = connect(mapStateToProps)(ContactCardContainerComponent);

export const TransientContactCardContainer = inLegacyTransientPanel(
  ContactCardContainer,
  ({panelId, parentPanelId, visualContact, location}) =>
    navigateContactCardAction(
      panelId,
      parentPanelId,
      visualContact.contact,
      visualContact.card?.id,
      location,
    ),
);
